import { isExpanded, setExpanded } from '../utils/dom.js';

// Written only when the visitor picks a language. The head script in
// layouts/_language_redirect.mustache reads it before anything renders.
const LANGUAGE_STORAGE_KEY = 'catch-lang-pref';

const LANGUAGE_SWITCHER_DISABLED = (() => {
  if (typeof window === 'undefined') return false;
  const value = window.CATCH_DISABLE_LANGUAGE_SWITCHER;
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  return Boolean(value);
})();

export const initLanguageSwitcher = () => {
  const desktopLanguageDropdown = document.getElementById('languageDropdown');
  const desktopLanguageMenu = document.getElementById('desktopLanguageMenu');
  const languageModalOverlay = document.getElementById('languageModalOverlay');
  const languageModalClose = document.getElementById('languageModalClose');
  const languageSaveButton = document.getElementById('languageSaveButton');
  const mobileLanguageDropdown = document.querySelector('.mobile-language-dropdown');
  const languageDropdownWrapper = document.querySelector('.language-dropdown-wrapper');
  let desktopLangOptions = Array.from(document.querySelectorAll('.desktop-lang-option'));
  let languageOptions = Array.from(document.querySelectorAll('.language-option'));

  if (LANGUAGE_SWITCHER_DISABLED) {
    [languageDropdownWrapper, mobileLanguageDropdown, languageModalOverlay].forEach((element) => {
      if (!element) return;
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
    });
    return;
  }

  const setCookie = (name, value) => {
    if (typeof document === 'undefined') return;
    const encoded = encodeURIComponent(value);
    document.cookie = `${name}=${encoded}; Path=/; Max-Age=31536000; SameSite=Lax`;
  };

  const setStoredLanguage = (lang) => {
    if (lang) {
      setCookie(LANGUAGE_STORAGE_KEY, lang);
    }
  };

  // The rendered page decides what the switcher shows; a stored preference only
  // drives the redirect that happens before render.
  const resolveActiveLanguage = (supported) => {
    const htmlLang = document.documentElement.getAttribute('lang');
    if (htmlLang && (!supported || supported.includes(htmlLang))) return htmlLang;

    const activeMobile = document.querySelector('.language-option.active');
    if (activeMobile?.dataset?.lang && (!supported || supported.includes(activeMobile.dataset.lang))) {
      return activeMobile.dataset.lang;
    }

    const activeDesktop = document.querySelector('.desktop-lang-option.active');
    if (activeDesktop?.dataset?.lang && (!supported || supported.includes(activeDesktop.dataset.lang))) {
      return activeDesktop.dataset.lang;
    }

    if (languageOptions.length > 0) {
      const lang = languageOptions[0].getAttribute('data-lang');
      if (lang && (!supported || supported.includes(lang))) return lang;
    }

    if (desktopLangOptions.length > 0) {
      const lang = desktopLangOptions[0].getAttribute('data-lang');
      if (lang && (!supported || supported.includes(lang))) return lang;
    }

    if (supported && supported.length > 0) {
      return supported[0];
    }

    return 'tr';
  };

  let currentLanguage = resolveActiveLanguage(null);
  let selectedLanguage = currentLanguage;

  const setActiveState = (nodes, lang) => {
    nodes.forEach((node) => {
      if (node.getAttribute('data-lang') === lang) {
        node.classList.add('active');
      } else {
        node.classList.remove('active');
      }
    });
  };

  const updateMobileLanguageDisplay = (lang) => {
    if (!mobileLanguageDropdown) return;

    const languageText = mobileLanguageDropdown.querySelector('.language-text');
    const languageFlag = mobileLanguageDropdown.querySelector('.flag-icon');
    const selectedOption = document.querySelector(`.language-option[data-lang="${lang}"]`);

    if (languageText && selectedOption) {
      const label = selectedOption.querySelector('.language-name');
      if (label) {
        languageText.textContent = label.textContent;
      }
    }

    if (languageFlag && selectedOption) {
      const flag = selectedOption.querySelector('.language-flag');
      if (flag) {
        const newFlag = flag.cloneNode(true);
        languageFlag.replaceWith(newFlag);
        newFlag.classList.add('flag-icon');
        newFlag.classList.remove('language-flag');
      }
    }
  };

  const updateDesktopLanguageDisplay = (lang) => {
    if (!desktopLanguageDropdown) return;

    const dropdownFlag = desktopLanguageDropdown.querySelector('.flag-icon');
    const selectedOption = document.querySelector(`.desktop-lang-option[data-lang="${lang}"]`);

    if (dropdownFlag && selectedOption) {
      const flag = selectedOption.querySelector('.desktop-lang-flag');
      if (flag) {
        const newFlag = flag.cloneNode(true);
        dropdownFlag.replaceWith(newFlag);
        newFlag.classList.add('flag-icon');
        newFlag.classList.remove('desktop-lang-flag');
      }
    }
  };

  const syncLanguageUI = (lang) => {
    if (!lang) return;
    setActiveState(languageOptions, lang);
    setActiveState(desktopLangOptions, lang);
    updateMobileLanguageDisplay(lang);
    updateDesktopLanguageDisplay(lang);
  };

  const getAlternateLink = (lang) => document.querySelector(`link[rel="alternate"][data-lang="${lang}"]`);

  const navigateToLanguage = (lang) => {
    if (!lang || typeof window === 'undefined') return;
    const link = getAlternateLink(lang);
    if (!link?.href) return;
    // Alternates are absolute production URLs; stay on the current origin so
    // preview and local builds do not jump to the live site.
    const target = `${window.location.origin}${new URL(link.href, window.location.origin).pathname}`;
    if (target === window.location.href) return;
    window.location.href = target;
  };

  const applyActiveLanguage = (lang) => {
    if (!lang) return;
    currentLanguage = lang;
    selectedLanguage = lang;
    syncLanguageUI(lang);
  };

  const commitLanguage = (lang) => {
    if (!lang) return;
    applyActiveLanguage(lang);
    setStoredLanguage(lang);
    navigateToLanguage(lang);
  };

  const refreshLanguageOptions = () => {
    desktopLangOptions = Array.from(document.querySelectorAll('.desktop-lang-option'));
    languageOptions = Array.from(document.querySelectorAll('.language-option'));
  };

  const applySupportedLanguages = (supported) => {
    if (!supported || supported.length === 0) return;
    desktopLangOptions.forEach((option) => {
      if (!supported.includes(option.getAttribute('data-lang'))) {
        option.remove();
      }
    });
    languageOptions.forEach((option) => {
      if (!supported.includes(option.getAttribute('data-lang'))) {
        option.remove();
      }
    });
    refreshLanguageOptions();
  };

  const loadSiteLanguages = async () => {
    const links = Array.from(document.querySelectorAll('link[rel="alternate"][data-lang]'));
    if (links.length === 0) return null;
    const supported = Array.from(
      new Set(
        links
          .map((link) => link.getAttribute('data-lang'))
          .filter(Boolean)
      )
    );
    if (supported.length === 0) return null;
    return { supported };
  };

  let supportedLanguages = null;

  loadSiteLanguages().then((languages) => {
    if (Array.isArray(languages?.supported)) {
      supportedLanguages = languages.supported;
      applySupportedLanguages(supportedLanguages);
      applyActiveLanguage(resolveActiveLanguage(supportedLanguages));
      return;
    }
    syncLanguageUI(currentLanguage);
  });

  if (desktopLanguageDropdown && desktopLanguageMenu) {
    desktopLanguageDropdown.addEventListener('click', () => {
      const isOpen = isExpanded(desktopLanguageDropdown);
      setExpanded(desktopLanguageDropdown, !isOpen);
      desktopLanguageMenu.classList.toggle('is-open', !isOpen);
    });

    document.addEventListener('click', (event) => {
      const isClickInsideMenu = desktopLanguageMenu.contains(event.target);
      const isClickOnDropdown = desktopLanguageDropdown.contains(event.target);
      const isOpen = isExpanded(desktopLanguageDropdown);

      if (!isClickInsideMenu && !isClickOnDropdown && isOpen) {
        setExpanded(desktopLanguageDropdown, false);
        desktopLanguageMenu.classList.remove('is-open');
      }
    });
  }

  desktopLangOptions.forEach((option) => {
    option.addEventListener('click', () => {
      const lang = option.getAttribute('data-lang');
      if (lang) {
        commitLanguage(lang);
      }

      if (desktopLanguageDropdown && desktopLanguageMenu) {
        setExpanded(desktopLanguageDropdown, false);
        desktopLanguageMenu.classList.remove('is-open');
      }
    });
  });

  if (mobileLanguageDropdown && languageModalOverlay) {
    mobileLanguageDropdown.addEventListener('click', () => {
      languageModalOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });
  }

  const closeLanguageModal = () => {
    if (languageModalOverlay) {
      languageModalOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  if (languageModalClose) {
    languageModalClose.addEventListener('click', closeLanguageModal);
  }

  if (languageModalOverlay) {
    languageModalOverlay.addEventListener('click', (event) => {
      if (event.target === languageModalOverlay) {
        closeLanguageModal();
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && languageModalOverlay && languageModalOverlay.classList.contains('is-open')) {
      closeLanguageModal();
    }
  });

  languageOptions.forEach((option) => {
    option.addEventListener('click', () => {
      const lang = option.getAttribute('data-lang');
      if (!lang) return;
      selectedLanguage = lang;
      setActiveState(languageOptions, lang);
    });
  });

  if (languageSaveButton) {
    languageSaveButton.addEventListener('click', () => {
      commitLanguage(selectedLanguage);
      closeLanguageModal();
    });
  }
};
